import { useContext, useEffect, useMemo, useState } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import {
  NavigationV2 as HHGNavigationLayout,
  Props as NavigationV2Props,
  NavMenuContentType,
} from '@hhgtech/hhg-components/navigation'
import { LOCALE } from '@hhgtech/hhg-components/types'
import Cookies from 'js-cookie'
import dynamic from 'next/dynamic'
import { shallow } from 'zustand/shallow'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import ErrorBoundary from 'components/templates/navigatorLayout/errorBoundary'
import { domainLocales } from 'domainLocales'
import { CustomerCartManager, GuestCartManager } from 'extentions/cartManager'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  HAS_FIND_MY_BOOKING,
  isEnableVaccination,
  LOGO,
} from 'interfaces/constants'
import { ContentSponsor, SiteWideBanner } from 'interfaces/types'
import { actionTypes } from 'state/actionTypes'
import { AppContext } from 'state/appContext'
import { useQuickBookingStore } from 'state/quickBooking'
import { State } from 'state/types'

import { useNavStore } from './store'

const SiteWide = dynamic(() =>
  import('components/molecules/siteWide').then((mod) => mod.SiteWide),
)
const StickySponsoredBy = dynamic(() =>
  import('components/molecules/sponsoredBy').then(
    (mod) => mod.StickySponsoredBy,
  ),
)

export type Props = {
  onTopBarAppears?: (b: boolean) => void
  isSponsored?: boolean
  sponsoredType?: 'article' | 'content'
  sponsoredBy?: ContentSponsor | ContentSponsor[]
  sitewideBanner?: SiteWideBanner | null
  languageOptions?: NavigationV2Props['languageOptions']
  dataNavMenuContent?: NavMenuContentType
  activeMenuService?: NavigationV2Props['activeMenuService']
  subHead?: JSX.Element
  isHomepage?: boolean
}

/**
 * Todo: Remove after demo 9/6/2025
 * !Hotfix change UI navigator for demo vaccination
 */

const customStyleVaccination = css`
  a[data-event-category='Chatbot AI'] {
    button {
      padding-left: 10px;
      padding-right: 12px;
      height: 36px;

      .mantine-Button-label {
        font-size: 13px;
        font-weight: 500;
        line-height: 22px;
        color: #111111;
      }
      .mantine-Button-leftIcon {
        margin-right: 2px;
        svg {
          width: 24px;
          height: 24px;
        }
      }
    }
  }

  input[name='search-input-menu-servicedesktop'].mantine-Input-input {
    border-color: #e4e8ec;
    border-radius: 12px;
  }
`

const HHGNavigationLayoutStyled = styled(HHGNavigationLayout)`
  ${isEnableVaccination ? customStyleVaccination : ''}
`

const MainNav = ({
  onTopBarAppears,
  sitewideBanner,
  isSponsored,
  sponsoredType,
  sponsoredBy,
  dataNavMenuContent: dataNavMenuContentProps,
  languageOptions,
  activeMenuService,
  subHead,
  isHomepage,
}: // hideWelcomePopup,
Props) => {
  const {
    locale,
    router: { asPath },
  } = useRouterExtend()
  const currentUrl = useCurrentUrl()
  // const { ref, inView } = useInView({
  //   rootMargin: `-120px 0px 0px 0px`,
  // })
  const { setIsNavCollapse, setIsNavBanner, setIsNavSponsor } = useNavStore(
    (state) => ({
      setIsNavCollapse: state.setIsNavCollapse,
      setIsNavBanner: state.setIsNavBanner,
      setIsNavSponsor: state.setIsNavSponsor,
    }),
    shallow,
  )

  const [closeSiteWide, setCloseSiteWide] = useState(false)
  const { state = {} as State, dispatch } = useContext(AppContext)
  const { setOpenQuickBookingDrawer } = useQuickBookingStore(
    (state) => state,
    shallow,
  )
  const {
    userInfo,
    // cartNumber
  } = state
  const isMobile = useIsMobile()

  const configs = useMemo(
    () => ({
      enableAppIntroPage: [LOCALE.Vietnam].includes(locale),
      enableProfileFindMyBooking:
        HAS_FIND_MY_BOOKING.includes(locale) && isMobile,
    }),
    [locale, isMobile],
  )

  const sponsoredList =
    sponsoredBy === undefined || Array.isArray(sponsoredBy)
      ? sponsoredBy
      : [sponsoredBy]

  const sitewideBannerShown = !!sitewideBanner && !closeSiteWide
  useEffect(() => {
    setIsNavSponsor(!!isSponsored)
  }, [isSponsored])
  useEffect(() => {
    setIsNavBanner(sitewideBannerShown)
  }, [sitewideBannerShown])

  useEffect(() => {
    if (sitewideBanner?.type) {
      if (
        Cookies.get(`discover-disable-sitewide-${sitewideBanner?.type}`) ===
        'true'
      ) {
        setCloseSiteWide(true)
      }
    }
  }, [sitewideBanner?.type])

  useEffect(() => {
    locale === LOCALE.Vietnam &&
      (async () => {
        if (userInfo?.id) {
          await CustomerCartManager.getCartId()
          CustomerCartManager.getCart().then((res) => {
            dispatch({
              type: actionTypes.UPDATE_CART,
              payload: res?.data?.items_qty || 0,
            })
          })
        } else {
          await GuestCartManager.getCartId()
          GuestCartManager.getCart().then((res) => {
            dispatch({
              type: actionTypes.UPDATE_CART,
              payload: res?.data?.items_qty || 0,
            })
          })
        }
      })()
  }, [userInfo?.id, asPath])

  const dataNavMenuContent = useMemo(() => {
    return {
      ...dataNavMenuContentProps,
      healthTools: {
        ...dataNavMenuContentProps?.healthTools,
        items: dataNavMenuContentProps?.healthTools?.items?.map((item) => ({
          ...item,
          icon: item.icon || LOGO,
        })),
        spotlights: dataNavMenuContentProps?.healthTools?.spotlights?.map(
          (item) => ({ ...item, icon: item.icon || LOGO }),
        ),
      },
    }
  }, [dataNavMenuContentProps])

  return (
    <ErrorBoundary>
      <HHGNavigationLayoutStyled
        siteType="helloSites"
        locale={locale}
        currentUrl={currentUrl}
        onLogout={() => {
          callApiNew(PATHS.USER_PROFILE.POST_LOGOUT, { method: 'POST' }, 'sso')
          dispatch({
            type: actionTypes.LOGOUT,
            payload: {
              domain: '.' + domainLocales[locale],
            },
          })
        }}
        ssrMenuContent={dataNavMenuContent || {}}
        onTopBarAppears={(b: boolean) => {
          onTopBarAppears?.(b)
          setIsNavCollapse(!b)
        }}
        userState={{ ...state, userInfo: userInfo || undefined } as any}
        isMobile={isMobile}
        isHomepage={isHomepage}
        // cartNumber={locale === LOCALE.Vietnam ? cartNumber : undefined}
        activeMenuService={activeMenuService || 'categories'}
        sitewideBanner={
          sitewideBanner ? (
            <SiteWide
              isMobile={isMobile}
              banner={sitewideBanner}
              onClose={() => setCloseSiteWide(true)}
            />
          ) : undefined
        }
        sitewideBannerShown={sitewideBannerShown}
        languageOptions={languageOptions}
        onBookingClick={(e) => {
          e.preventDefault()
          // import('components/organisms/careQuickBookingPopup/helper').then(
          //   (mod) => {
          //     mod.carePostMessage({
          //       isCareQuickBookingPopupOpen: true,
          //       type: 'care-booking',
          //       messageFrom: 'hhg-header',
          //       targetWindow: 'self',
          //       isLoggedIn: !!state?.userInfo?.id,
          //     })
          //   },
          // )
          setOpenQuickBookingDrawer(true)
        }}
        sponsorComponent={
          isSponsored ? (
            <StickySponsoredBy
              isCollapsed={false}
              sponsoredType={sponsoredType}
              sponsoredBy={sponsoredList}
              isMobile={isMobile}
            />
          ) : undefined
        }
        isUserLoading={state?.userInfo === undefined}
        subHead={subHead}
        configs={configs}
      />
    </ErrorBoundary>
  )
}

export { MainNav }
